import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import type { CardScope } from './credit'

/**
 * Kart ekstresi / kredi / nakit avans son ödeme hatırlatmaları. Şirket ayarı ödeme yetkililerine giden hatırlatmaları,
 * kişisel ayar kişinin kendi kart ve kredileri için (isterse kendi e-posta adresine) gidenleri belirler.
 */
export const REMINDER_SETTINGS_PERM = 'finance:credit_reminder:settings'
export const BEFORE_CHOICES = [14, 7, 5, 3, 2, 1, 0]
export const OVERDUE_CHOICES = [1, 2, 3, 5, 7, 14, 30]

export type ReminderSettings = {
  enabled: boolean
  daysBefore: number[]
  overdueDays: number[]
  emailEnabled: boolean
  emailTo: string
  accountEmail: string
}

type Row = Record<string, unknown>
const nums = (v: unknown) => (Array.isArray(v) ? v.map(Number).filter((n) => Number.isFinite(n)) : [])
const key = (scope: CardScope) => ['finance', 'credit-reminders', scope] as const

export const useReminderSettings = (scope: CardScope, enabled = true) =>
  useQuery({
    queryKey: key(scope),
    enabled,
    queryFn: async () => {
      const r = await api.get<Row>('/api/v1/finance/credit-reminders/settings', { scope })
      return {
        enabled: r.enabled !== false,
        daysBefore: nums(r.days_before),
        overdueDays: nums(r.overdue_days),
        emailEnabled: r.email_enabled === true,
        emailTo: String(r.email_to ?? ''),
        accountEmail: String(r.account_email ?? ''),
      } as ReminderSettings
    },
  })

export const useSaveReminderSettings = (scope: CardScope) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (s: ReminderSettings) =>
      api.put(`/api/v1/finance/credit-reminders/settings?scope=${scope}`, {
        enabled: s.enabled,
        days_before: s.daysBefore,
        overdue_days: s.overdueDays,
        email_enabled: s.emailEnabled,
        email_to: s.emailTo.trim(),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(scope) }),
  })
}

export const useSendReminderTest = () =>
  useMutation({
    mutationFn: async () =>
      (await api.post<{ sent_to?: string }>('/api/v1/finance/credit-reminders/test', {})).sent_to ?? '',
  })
